/**
 * AAIKO collection filter drawer.
 *
 * The section renders a working GET <form> pointed at the collection URL, so
 * filtering works with this file absent. Everything here is enhancement:
 *
 *   - overlay behaviour (open/close, scrim, Escape, focus trap, focus return)
 *   - live counts: every change re-fetches this section through Shopify's
 *     Section Rendering API and swaps the panel, so option counts and the
 *     "View N products" label stay truthful without leaving the page
 *   - no-reload apply: submitting swaps the collection grid in place when a
 *     grid container is found, otherwise it falls back to a normal navigation
 *
 * The drawn trigger lives in the collection toolbar, which this component does
 * not own. Any element in the document carrying [data-aaiko-filters-toggle] or
 * [data-aaiko-filter-toggle] opens the drawer. If nothing does, the section's
 * own fallback trigger is revealed so the drawer is never unreachable.
 */
(() => {
  // The collection toolbar (another owner) ships data-aaiko-filter-toggle;
  // this section's own fallback uses data-aaiko-filters-toggle. Accept both.
  const TOGGLE = '[data-aaiko-filters-toggle], [data-aaiko-filter-toggle]';

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  // Candidates for the grid container, in priority order. The collection agent
  // owns the grid; the first attribute is the one we ask them to add.
  const GRID_SELECTORS = [
    '[data-aaiko-collection-grid]',
    '.shopify-section:has(> aaiko-collection-grid)',
    '.shopify-section:has(> .aaiko-collection-grid)',
    '#ProductGridContainer',
    '.shopify-section--main-collection',
    '.shopify-section:has(> .section--main-collection)',
  ];

  function findGrid(root = document) {
    for (const sel of GRID_SELECTORS) {
      let el = null;
      try {
        el = root.querySelector(sel);
      } catch (e) {
        el = null;
      }
      if (el) return { el, sel };
    }
    return null;
  }

  class AaikoFilters extends HTMLElement {
    connectedCallback() {
      if (this.__booted) return;
      this.__booted = true;

      this.panel = this.querySelector('[data-aaiko-filters-panel]');
      this.scrim = this.querySelector('[data-aaiko-filters-scrim]');
      this.opener = null;
      this.sectionId = this.dataset.sectionId;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      this.addEventListener('change', this.onChange);
      this.addEventListener('toggle', this.onGroupToggle, true);
      if (this.panel) this.panel.addEventListener('submit', this.onSubmit);

      this.revealFallbackTrigger();

      // Deep link: /collections/x#AaikoFilterPanel opens the drawer.
      if (this.panel && window.location.hash === '#' + this.panel.id) this.open();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* ---------------- open / close ---------------- */

    get isOpen() {
      return this.dataset.open === 'true';
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || null;
      this.dataset.open = 'true';
      // Horizon scrolls inside .page-wrapper, so locking documentElement does
      // nothing. Lock through an attribute this component owns rather than an
      // inline style: the cart drawer and search panel save/restore inline
      // overflow on the same elements, and two independent inline stacks
      // corrupt each other. An attribute + CSS rule cannot.
      document.documentElement.setAttribute('data-aaiko-filters-open', '');
      this.syncToggles(true);
      // Focus the dialog itself, not its first control: the drawn header has no
      // focus state and landing on "Clear all" reads as a selected action.
      if (this.panel) this.panel.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      this.dataset.open = 'false';
      document.documentElement.removeAttribute('data-aaiko-filters-open');
      this.syncToggles(false);
      if (this.panel && window.location.hash === '#' + this.panel.id) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      if (this.opener && document.contains(this.opener)) {
        this.opener.focus({ preventScroll: true });
      }
      this.opener = null;
    }

    syncToggles(expanded) {
      document.querySelectorAll(TOGGLE).forEach((el) => {
        el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    revealFallbackTrigger() {
      const fallback = this.querySelector('[data-aaiko-filters-fallback]');
      if (!fallback) return;
      const external = document.querySelector(
        '[data-aaiko-filters-toggle]:not([data-aaiko-filters-fallback]), [data-aaiko-filter-toggle]:not([data-aaiko-filters-fallback])'
      );
      if (!external) fallback.removeAttribute('hidden');
    }

    onDocumentClick(event) {
      const toggle = event.target.closest && event.target.closest(TOGGLE);
      if (toggle) {
        event.preventDefault();
        if (this.isOpen) this.close();
        else this.open(toggle);
        return;
      }
      if (!this.isOpen) return;
      if (event.target.closest('[data-aaiko-filters-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      if (this.scrim && event.target === this.scrim) this.close();
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.panel) return;

      const nodes = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onGroupToggle(event) {
      const summary = event.target.querySelector && event.target.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', event.target.open ? 'true' : 'false');
    }

    /* ---------------- URL building ---------------- */

    buildURL() {
      if (!this.panel) return window.location.href;
      const data = new FormData(this.panel);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) {
        if (value === '' || value == null) continue;
        params.append(key, value);
      }
      const qs = params.toString();
      return this.panel.getAttribute('action') + (qs ? '?' + qs : '');
    }

    /* ---------------- live counts ---------------- */

    onChange() {
      if (!this.sectionId) return;
      clearTimeout(this.__refreshTimer);
      this.__refreshTimer = setTimeout(() => this.refresh(), 250);
    }

    async refresh() {
      const url = this.buildURL();
      const sep = url.includes('?') ? '&' : '?';
      const token = (this.__token = Symbol('refresh'));
      let html;
      try {
        const response = await fetch(url + sep + 'section_id=' + encodeURIComponent(this.sectionId));
        if (!response.ok) return;
        html = await response.text();
      } catch (e) {
        return;
      }
      if (token !== this.__token || !this.panel) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-aaiko-filters]');
      if (!next) return;

      const openGroups = new Set(
        Array.from(this.querySelectorAll('[data-aaiko-filters-group][open]')).map(
          (el) => el.dataset.aaikoFiltersGroup
        )
      );
      const scroll = this.panel.scrollTop;
      const prevCountEl = this.querySelector('[data-aaiko-filters-count]');
      const prevCount = prevCountEl ? prevCountEl.textContent.trim() : null;
      const activeName = document.activeElement && document.activeElement.name;
      const activeValue = document.activeElement && document.activeElement.value;

      // Replace the panel body only: the host element keeps its listeners.
      this.innerHTML = next.innerHTML;
      this.panel = this.querySelector('[data-aaiko-filters-panel]');
      this.scrim = this.querySelector('[data-aaiko-filters-scrim]');
      if (this.panel) {
        this.panel.addEventListener('submit', this.onSubmit);
        this.panel.scrollTop = scroll;
      }
      this.revealFallbackTrigger();
      this.syncToggles(this.isOpen);

      this.querySelectorAll('[data-aaiko-filters-group]').forEach((el) => {
        const wanted = openGroups.has(el.dataset.aaikoFiltersGroup);
        if (el.open !== wanted) el.open = wanted;
        const summary = el.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', el.open ? 'true' : 'false');
      });

      // Fade the result count in when a filter actually changed it. Only ever
      // runs off a change event, never on load, so it is a state change rather
      // than an entrance animation. Skipped when the number is the same.
      const nextCountEl = this.querySelector('[data-aaiko-filters-count]');
      if (nextCountEl && prevCount !== null && nextCountEl.textContent.trim() !== prevCount) {
        nextCountEl.classList.add('is-swapping');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => nextCountEl.classList.remove('is-swapping'));
        });
      }

      if (activeName && this.panel) {
        const restored = this.panel.querySelector(
          '[name="' + CSS.escape(activeName) + '"][value="' + CSS.escape(activeValue || '') + '"]'
        );
        if (restored) restored.focus({ preventScroll: true });
      }
    }

    /* ---------------- apply ---------------- */

    async onSubmit(event) {
      const grid = findGrid();
      const url = this.buildURL();
      if (!grid) return; // let the browser navigate
      event.preventDefault();

      let html;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);
        html = await response.text();
      } catch (e) {
        window.location.assign(url);
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector(grid.sel);
      if (!nextGrid) {
        window.location.assign(url);
        return;
      }
      grid.el.replaceWith(nextGrid);
      history.pushState({ aaikoFilters: true }, '', url);
      this.close();
      this.refresh();
      window.dispatchEvent(new CustomEvent('aaiko:filters:applied', { detail: { url } }));
    }
  }

  if (!customElements.get('aaiko-filters')) {
    customElements.define('aaiko-filters', AaikoFilters);
  }
})();
